import { create } from 'zustand';
import type {
  ConnectionMetaInfo,
  PinnedObjectRef,
  UpdatePinPatch,
} from '../../../types/electron';
import { pinRefKey } from '../services/pinnedObjects/pinRef';
import { mergeVisibleReorder } from '../services/pinnedObjects/pinnedReorderHandler';

const EMPTY: ConnectionMetaInfo = { version: 2, pinnedObjects: [] };

type State = {
  byConnection: Record<string, ConnectionMetaInfo>;
  /**
   * ConnectionIds we've already subscribed onChange for. Updated via `set()`
   * with a fresh Set instance so the membership-check is observable across
   * concurrent `hydrate()` calls — the previous in-place `.add()` happened
   * AFTER an `await`, letting two callers both pass the empty check and
   * register duplicate `onChange` listeners (F13).
   */
  _subscribed: Set<string>;
  hydrate: (connectionId: string) => Promise<void>;
  pin: (connectionId: string, ref: PinnedObjectRef) => Promise<void>;
  unpin: (connectionId: string, ref: PinnedObjectRef) => Promise<void>;
  reorder: (connectionId: string, ordered: PinnedObjectRef[]) => Promise<void>;
  /**
   * Atomically rename/edit a single workspace pin in place (F4). No
   * optimistic local write — the `onChange` broadcast lands within one IPC
   * round-trip and rename/edit isn't drag-latency-sensitive like reorder.
   */
  updatePin: (connectionId: string, pinId: string, patch: UpdatePinPatch) => Promise<void>;
  /**
   * Release in-memory state + the IPC `onChange` subscription for a connection
   * that is being removed (F15). Wired from `databaseStore.removeConnection`
   * so stale pins and orphaned listeners don't survive a disconnect.
   */
  forget: (connectionId: string) => void;
};

function api() {
  return (window as unknown as {
    electronAPI?: { connectionMetaInfo?: {
      get: (cid: string) => Promise<ConnectionMetaInfo>;
      pinObject: (cid: string, ref: PinnedObjectRef) => Promise<ConnectionMetaInfo>;
      unpinObject: (cid: string, ref: PinnedObjectRef) => Promise<ConnectionMetaInfo>;
      reorderPins: (cid: string, orderedKeys: string[]) => Promise<ConnectionMetaInfo>;
      updatePin: (cid: string, pinId: string, patch: UpdatePinPatch) => Promise<ConnectionMetaInfo>;
      onChange: (cb: (cid: string, info: ConnectionMetaInfo) => void) => () => void;
    } };
  }).electronAPI?.connectionMetaInfo;
}

/**
 * Module-level so it can hold non-serialisable handles (the `() => void`
 * returned by `ipc.onChange`). Keyed by connectionId so `forget(cid)` can
 * release exactly one listener per id.
 */
const unsubscribers = new Map<string, () => void>();

/**
 * G6 — `hydrate`'s `ipc.get(connectionId)` is async. If the user switches
 * connections quickly (or a slow `get` is in flight when a mutation
 * broadcasts via `onChange`), the stale `get()` response can resolve AFTER
 * a newer `onChange` snapshot and clobber it with older data. Each
 * connectionId gets a generation counter bumped on every `onChange`
 * delivery (and cleared on `forget`); `hydrate` captures the generation
 * right before its `await` and refuses to `set()` if the generation moved
 * on while it was waiting.
 *
 * Exported ONLY so the unit-test `beforeEach` can `clear()` it between tests
 * (module-level state otherwise leaks across cases) — production code must
 * never touch it directly.
 */
export const generations = new Map<string, number>();

export const usePinnedObjectsStore = create<State>((set, get) => ({
  byConnection: {},
  _subscribed: new Set<string>(),

  hydrate: async (connectionId) => {
    const ipc = api();
    if (!ipc) {
      set((s) => ({ byConnection: { ...s.byConnection, [connectionId]: EMPTY } }));
      return;
    }

    // F13 — Subscribe & claim the `_subscribed` slot SYNCHRONOUSLY, before the
    // `await ipc.get(...)`. The previous code did this AFTER the await, so
    // two interleaving `hydrate(cid)` calls both saw an empty set, both
    // called `ipc.onChange`, and every later mutation fired the store
    // callback N times. Doing the work synchronously closes the race window
    // entirely; we also use `set(...)` with a fresh Set so the membership
    // update is observable across all callers.
    const wasAlreadySubscribed = get()._subscribed.has(connectionId);
    if (!wasAlreadySubscribed) {
      set((s) => {
        const next = new Set(s._subscribed);
        next.add(connectionId);
        return { _subscribed: next };
      });
      const unsubscribe = ipc.onChange((cid, info) => {
        // H7 — the IPC `onChange` is a GLOBAL broadcast: every connection's
        // listener receives every connection's events. Without this guard, N
        // connections process each event N times (N× redundant `set()` +
        // generation bumps). Each listener owns exactly ONE connectionId
        // (F13 registers one per id), so drop events for any other cid first.
        if (cid !== connectionId) return;
        // Late `onChange` events from the still-attached IPC listener that
        // arrive AFTER `forget(cid)` must NOT silently resurrect the dropped
        // slot — F15 enforces this contract.
        if (!get()._subscribed.has(cid)) return;
        // G6 — bump the generation BEFORE writing so any `hydrate(cid)`
        // whose `ipc.get` is still in flight sees a mismatch when it wakes
        // up and skips its now-stale write.
        generations.set(cid, (generations.get(cid) ?? 0) + 1);
        set((s) => ({ byConnection: { ...s.byConnection, [cid]: info } }));
      });
      unsubscribers.set(connectionId, unsubscribe);
    }

    // Review finding #10 — `hydrate` is called from a useEffect keyed on the
    // `activeConnections` array reference. If the parent rebuilds the array
    // on unrelated state changes, the effect re-fires every render. The
    // listener-subscribe guard above is enough to prevent the listener leak,
    // but EVERY re-fire still issued a fresh IPC `get` and wrote the same
    // bytes into `byConnection[cid]`, retriggering all store subscribers.
    // Skip the IPC roundtrip once we've already loaded this connection AND
    // we still hold the live `onChange` subscription — the live listener
    // keeps the slot in sync from there on.
    if (wasAlreadySubscribed && get().byConnection[connectionId]) {
      return;
    }

    // G6 — capture the generation immediately before the async gap. If an
    // `onChange` broadcast lands (and bumps it) while this `get()` is in
    // flight, the generation check below discards this now-stale response
    // instead of clobbering the newer snapshot `onChange` already wrote.
    const genAtRequest = generations.get(connectionId) ?? 0;
    const info = await ipc.get(connectionId);
    // F15 × G6 / H2 — `forget(connectionId)` may have disconnected this
    // connection while the `get()` above was in flight. `forget` now BUMPS the
    // generation (monotonic epoch) rather than deleting it, so the generation
    // check below already catches a forget-then-rehydrate ABA on a FIRST
    // hydrate (delete reset the counter to `?? 0` == the captured `genAtRequest`
    // of 0, letting the stale get() slip through). We ALSO recheck subscription
    // liveness as belt-and-braces — the same condition the onChange path
    // enforces — so a late `get()` never resurrects a forgotten connection's slot.
    if (!get()._subscribed.has(connectionId)) return;
    if ((generations.get(connectionId) ?? 0) !== genAtRequest) return;
    set((s) => ({ byConnection: { ...s.byConnection, [connectionId]: info } }));
  },

  pin: async (cid, ref) => { await api()?.pinObject(cid, ref); },
  unpin: async (cid, ref) => { await api()?.unpinObject(cid, ref); },
  reorder: async (cid, ordered) => {
    // Review finding #7 — apply the new order LOCALLY first so the tree
    // re-renders in the dropped position immediately. Without this, the tree
    // snapped back to the old order between drop and IPC return (~10-100ms),
    // then jumped to the new position when `onChange` arrived. The onChange
    // arrival is now a no-op when the optimistic state already matches.
    const prev = get().byConnection[cid];
    // I1 — `ordered` is derived from the RENDERED pinned children, which (since
    // H1) exclude workspace pins. Stable-merge the visible reordering into the
    // FULL authoritative list — hidden pins keep their original indexes, visible
    // pins fill the remaining slots in their new relative order — so neither the
    // optimistic install nor the IPC keys drop the hidden pins (the backend
    // would otherwise correctly reject a reduced array as not-a-permutation).
    // Task 15 renders workspace pins (making visible = full again); the merge
    // stays as defense for any future filtered kind.
    const full = prev ? mergeVisibleReorder(prev.pinnedObjects, ordered) : [...ordered];
    set((s) => ({
      byConnection: {
        ...s.byConnection,
        [cid]: { version: 2, pinnedObjects: full },
      },
    }));

    // F8 — In multi-window mode another window may have mutated the pinned
    // set between the moment this window computed `ordered` and the moment
    // the IPC call lands. The backend then rejects (the set is no longer a
    // permutation). On rejection we MUST refresh this window's snapshot from
    // disk so the UI stops showing a stale view, then re-throw so the caller
    // can surface a toast / log the failure.
    //
    // F1 — only identity KEYS cross the IPC. The main process reorders its
    // own authoritative refs by key, so this window's (possibly stale) pin
    // names/payloads can never overwrite what another window saved.
    try {
      await api()?.reorderPins(cid, full.map(pinRefKey));
    } catch (err) {
      // I2 — `forget(cid)` may have disconnected this connection while the
      // `reorderPins` call above was pending. Recovery would then RESURRECT
      // it: `hydrate(cid)` re-subscribes the removed connection's onChange
      // listener, and if the read then failed, the slot-absent restore below
      // would re-install `prev` — stale state + a live listener for a
      // connection the app no longer has. If we're no longer subscribed, skip
      // recovery entirely (forget already cleaned the slot) and just re-throw.
      if (!get()._subscribed.has(cid)) throw err;
      // Drop the slot so the upcoming hydrate is forced to issue a fresh IPC
      // `get` (the #10 short-circuit otherwise treats the optimistic state as
      // already-loaded and skips the round-trip — exactly the wrong call
      // after a rejection where we need to catch up with what the OTHER
      // window wrote).
      set((s) => {
        const next = { ...s.byConnection };
        delete next[cid];
        return { byConnection: next };
      });
      try { await get().hydrate(cid); } catch (rehydrateErr) {
        console.error('[pinnedObjectsStore] reorder: re-hydrate after rejection failed:', rehydrateErr);
        // If the rehydrate itself fails (truly offline), restore the prior
        // snapshot so the UI doesn't stay blank — but ONLY if the slot is still
        // empty (H3) AND the connection is still subscribed (I2 — forget may
        // have landed during the failed rehydrate; restoring would resurrect a
        // forgotten connection's state). A concurrent `onChange` can land a
        // NEWER authoritative snapshot between the slot-delete above and this
        // failure; blindly restoring `prev` would clobber that fresher
        // broadcast with stale data.
        if (prev && get()._subscribed.has(cid) && !get().byConnection[cid]) {
          set((s) => ({ byConnection: { ...s.byConnection, [cid]: prev } }));
        }
      }
      throw err;
    }
  },

  // F4 — atomic, position-preserving rename/edit. No optimistic write: the
  // `onChange` broadcast lands within one IPC round-trip and rename/edit
  // isn't drag-latency-sensitive the way reorder is.
  updatePin: async (cid, pinId, patch) => { await api()?.updatePin(cid, pinId, patch); },

  forget: (connectionId) => {
    const off = unsubscribers.get(connectionId);
    if (off) {
      try { off(); } catch (err) {
        console.error('[pinnedObjectsStore] forget: onChange unsubscribe threw:', err);
      }
      unsubscribers.delete(connectionId);
    }
    // H2 — BUMP (don't delete) the generation. Deleting reset it to absent
    // (`?? 0`), so a forget-then-immediate-rehydrate reused epoch 0: a first
    // hydrate's still-in-flight `get()` (captured genAtRequest 0) then passed
    // the `0 === 0` generation check after re-subscribe and installed stale
    // data (an ABA race). A monotonically increasing epoch can never collide
    // with a prior in-flight request's captured value, so the stale write is
    // always discarded.
    generations.set(connectionId, (generations.get(connectionId) ?? 0) + 1);
    set((s) => {
      const nextByConn = { ...s.byConnection };
      delete nextByConn[connectionId];
      const nextSubscribed = new Set(s._subscribed);
      nextSubscribed.delete(connectionId);
      return { byConnection: nextByConn, _subscribed: nextSubscribed };
    });
  },
}));
